import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Building2, CalendarCheck, Tag } from "lucide-react";

const SHEET_SPRING = { type: "spring", damping: 30, stiffness: 320 };

function useBodyScrollLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

function usePortalTarget() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? document.body : null;
}

export const TaskPopupFrame = ({
  isOpen,
  onClose,
  title,
  titleHighlight = null,
  titleIcon = null,
  subtitle = null,
  maxWidthClass = "max-w-5xl",
  children,
  footer = null,
}) => {
  const portalTarget = usePortalTarget();
  useBodyScrollLock(isOpen);

  if (!portalTarget) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            key="task-popup-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              key="task-popup-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={SHEET_SPRING}
              className={`pointer-events-auto flex h-[90dvh] w-full ${maxWidthClass} flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="shrink-0 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-4 pt-3 pb-4 sm:px-7 sm:py-5">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200 sm:hidden" />

                <div className="flex items-start justify-between gap-3 sm:items-center">
                  <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                    {titleIcon ? (
                      <div className="shrink-0 rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                        {titleIcon}
                      </div>
                    ) : null}
                    <div className="min-w-0">
                      <h2 className="text-base font-black uppercase leading-snug tracking-tight text-gray-800 sm:text-lg md:text-xl">
                        {title}{" "}
                        {titleHighlight ? (
                          <span className="text-red-700">{titleHighlight}</span>
                        ) : null}
                      </h2>
                      {subtitle ? (
                        <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                          {subtitle}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    aria-label="Đóng"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain bg-[#F8FAFC] p-4 sm:p-7">
                {children}
              </div>

              {footer ? (
                <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-7">
                  {footer}
                </div>
              ) : null}
            </motion.div>
          </div>
        </>
      ) : null}
    </AnimatePresence>,
    portalTarget
  );
};

/** Sheet nhập lý do / xác nhận — mobile bottom sheet, desktop centered */
export function PopupPromptSheet({
  isOpen,
  onClose,
  title,
  value,
  onChange,
  onConfirm,
  loading = false,
  confirmLabel = "Xác nhận",
  cancelLabel = "Hủy",
  placeholder = "Nhập nội dung...",
}) {
  const portalTarget = usePortalTarget();
  useBodyScrollLock(isOpen);

  if (!portalTarget) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            key="prompt-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[220] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[230] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              key="prompt-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={SHEET_SPRING}
              className="pointer-events-auto w-full max-w-md overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white p-5 shadow-2xl sm:rounded-[1.5rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-200 sm:hidden" />
              <h3 className="mb-3 text-base font-black uppercase tracking-tight text-gray-800">
                {title}
              </h3>
              <textarea
                rows={3}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={placeholder}
                className="mb-4 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-red-300 focus:ring-4 focus:ring-red-50"
              />
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-gray-600 hover:bg-gray-100"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={loading}
                  className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-wider text-white ${
                    loading
                      ? "cursor-not-allowed bg-red-400"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      ) : null}
    </AnimatePresence>,
    portalTarget
  );
}

/** Xem ảnh phóng to — phủ toàn màn hình mobile */
export function PopupImagePreview({ src, onClose }) {
  const portalTarget = usePortalTarget();
  useBodyScrollLock(!!src);

  if (!portalTarget || !src) return null;

  return createPortal(
    <AnimatePresence>
      {src ? (
        <motion.div
          key="image-preview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[240] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.img
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            src={src}
            alt="Phóng to ảnh"
            className="max-h-[90vh] max-w-[90vw] rounded-lg border-4 border-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>,
    portalTarget
  );
}

const InfoItem = ({ icon: Icon, label, value, colorClass }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <span className="text-[9px] font-black uppercase tracking-tighter text-gray-400">
      {label}
    </span>
    <div className="flex min-w-0 items-center gap-2">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${colorClass}`} strokeWidth={2.25} />
      <span className="truncate text-xs font-bold text-gray-700">{value || "—"}</span>
    </div>
  </div>
);

/** Khối thông tin nhân viên — gần giống Chi tiết hoàn thành */
export const PopupEmployeeInfoCard = ({
  branchName,
  employeeName,
  workDay,
  taskTypeLabel,
}) => (
  <div className="grid grid-cols-2 gap-4 rounded-[1.5rem] border border-gray-300 bg-white p-4 shadow-sm sm:rounded-[2rem] sm:p-5">
    <InfoItem
      icon={Building2}
      label="Chi nhánh"
      value={branchName}
      colorClass="text-purple-500"
    />
    <InfoItem
      icon={User}
      label="Nhân viên"
      value={employeeName}
      colorClass="text-blue-500"
    />
    <InfoItem
      icon={CalendarCheck}
      label="Ngày làm"
      value={workDay}
      colorClass="text-green-500"
    />
    <InfoItem
      icon={Tag}
      label="Loại"
      value={taskTypeLabel}
      colorClass="text-orange-500"
    />
  </div>
);

export const resolveTaskTypeLabel = (items = []) => {
  const types = items
    .map((t) => {
      if (!t || typeof t === "string") return "";
      return String(t.task_type || "").toLowerCase();
    })
    .filter(Boolean);
  const uniq = [...new Set(types)];
  if (uniq.length === 0) return "—";
  if (uniq.length > 1) return "Hỗn hợp";
  return uniq[0] === "assigned" ? "Giao việc" : "Đăng ký";
};

export const PopupSectionCard = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-gray-300 bg-white p-4 transition-all hover:border-red-300 hover:shadow-md sm:p-5 ${className}`}
  >
    {children}
  </div>
);

export const PopupActionButton = ({
  children,
  onClick,
  disabled,
  variant = "primary",
  className = "",
  type = "button",
}) => {
  const base =
    "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition disabled:opacity-60 active:scale-[0.98]";
  const styles =
    variant === "danger-outline"
      ? "border border-red-300 text-red-700 hover:bg-red-50"
      : variant === "success-outline"
      ? "border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      : "bg-gradient-to-r from-red-700 to-red-800 text-white shadow-sm shadow-red-100 hover:shadow-md";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles} ${className}`}
    >
      {children}
    </button>
  );
};

export const PopupStatusBadge = ({ status }) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
    rejected: "bg-red-50 text-red-700 border-red-300",
    waiting: "bg-amber-50 text-amber-700 border-amber-300",
    reported: "bg-blue-50 text-blue-700 border-blue-300",
  };
  const textMap = {
    approved: "Đã duyệt",
    rejected: "Không duyệt",
    waiting: "Chờ duyệt",
    reported: "Đã báo cáo",
  };
  const cls = map[status] || "bg-gray-50 text-gray-700 border-gray-300";
  const text = textMap[status] || status || "Không xác định";

  return (
    <span
      className={`inline-flex shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${cls}`}
    >
      {text}
    </span>
  );
};
